// Shared income-statement graph builder.
// Used by both the desktop Sankey chart and the mobile horizontal bar chart so
// flow-type rules, label shortening, and remainder/convergence math stay in one
// place.

const LABEL_MAP = {
  "Income Loss Before Income Taxes Extraordinary Items": "Income (Loss) before Taxes",
  "Net Income Loss": "Net Income",
  "Selling General And Administrative Expenses": "SG&A Expenses",
  "Research And Development Expenses": "R&D Expenses",
  "Operating Income Loss": "Operating Income",
};

export const GREEN_NODES = new Set([
  // Standard company concepts
  "Revenue",
  "Gross Profit",
  "Operating Income",
  "Income (Loss) before Taxes",
  "Net Income",
  // Bank/financial company concepts
  "Interest Income",
  "Net Interest Income",
  "Noninterest Revenue",
  "Total Net Revenue",
  "Pre-tax Income",
  // Insurance company concepts
  "Premiums Earned",
  "Total Revenue",
]);

export function formatConceptLabel(name) {
  if (!name) return name;
  return LABEL_MAP[name] || name;
}

/**
 * Build the enriched value map for a single period.
 * Computes missing remainder/convergence values from sankey-map relationships.
 *
 * @param {Array} periodData - income-statement rows for a single (fy, fp) period
 * @param {Array} sankeyMapData - rows from /sankey-map
 * @returns {Object} valueMap keyed by concept name
 */
export function buildValueMap(periodData, sankeyMapData) {
  const valueMap = {};
  periodData.forEach(item => {
    if (item.value !== null && item.value !== undefined && !isNaN(item.value) && item.value !== 0) {
      valueMap[item.concept] = item.value;
    }
  });

  if (!sankeyMapData || sankeyMapData.length === 0) return valueMap;

  const stepLevels = [...new Set(sankeyMapData.map(m => m.step_level))].sort((a, b) => a - b);

  stepLevels.forEach(level => {
    const mappingsAtLevel = sankeyMapData.filter(m => m.step_level === level);

    // Outflow/remainder enrichment per source
    const bySource = {};
    mappingsAtLevel.forEach(m => {
      const src = m.source_concept || '__root__';
      if (!bySource[src]) bySource[src] = [];
      bySource[src].push(m);
    });

    Object.entries(bySource).forEach(([src, mappings]) => {
      if (src === '__root__') return;
      const sourceVal = valueMap[src];
      if (!sourceVal || sourceVal <= 0) return;

      const outflows = mappings.filter(m => m.flow_type === 'outflow');
      const remainders = mappings.filter(m => m.flow_type === 'remainder');

      let totalOutflow = 0;
      outflows.forEach(m => {
        if (valueMap[m.target_concept] && valueMap[m.target_concept] > 0) {
          totalOutflow += valueMap[m.target_concept];
        }
      });

      remainders.forEach(m => {
        if (!valueMap[m.target_concept] || valueMap[m.target_concept] === 0) {
          const computed = sourceVal - totalOutflow;
          if (computed > 0) valueMap[m.target_concept] = computed;
        }
      });
    });

    // Convergence: multiple sources feeding into one target
    const byTarget = {};
    mappingsAtLevel.forEach(m => {
      if (!m.source_concept) return;
      if (!byTarget[m.target_concept]) byTarget[m.target_concept] = [];
      byTarget[m.target_concept].push(m);
    });

    Object.entries(byTarget).forEach(([target, mappings]) => {
      if (mappings.length > 1 && (!valueMap[target] || valueMap[target] === 0)) {
        let total = 0;
        let hasAny = false;
        mappings.forEach(m => {
          if (valueMap[m.source_concept] && valueMap[m.source_concept] > 0) {
            total += valueMap[m.source_concept];
            hasAny = true;
          }
        });
        if (hasAny && total > 0) valueMap[target] = total;
      }
    });
  });

  return valueMap;
}

/**
 * Filter income-statement rows to a single (fy, fp) period.
 * Returns the period rows + a label like "2026 Q1" or "2025".
 */
export function selectPeriod(data, fy, fp) {
  const periodData = data.filter(d => {
    if (fp) return d.fy === fy && d.fp === fp;
    return d.fy === fy;
  });
  const label = fp ? `${fy} ${fp}` : `${fy}`;
  return { periodData, label };
}

/**
 * Find the latest period (most recent fy + fp) in the income-statement rows.
 * Quarterly: returns {fy, fp}; annual: {fy, fp: null}.
 */
export function findLatestPeriod(data, isQuarterly) {
  const periods = new Set();
  data.forEach(d => {
    const key = isQuarterly && d.fp ? `${d.fy}|${d.fp}` : `${d.fy}|`;
    periods.add(key);
  });
  if (periods.size === 0) return null;

  const sorted = [...periods].sort((a, b) => {
    const [yearA, fpA] = a.split('|');
    const [yearB, fpB] = b.split('|');
    if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
    const order = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
    return (order[fpB] || 0) - (order[fpA] || 0);
  });

  const [fy, fp] = sorted[0].split('|');
  return { fy, fp: fp || null };
}

// ── Revenue / EPS history (mirrors earnings-shorts/db_queries.py logic) ──────
// Pivots income-statement rows into per-period summary records suitable for
// QoQ / YoY KPI computation.
const REVENUE_CONCEPTS_PRIORITY = [
  "Revenue",
  "Total Revenue",
  "Net Revenue",
];

function resolveRevenue(perConcept) {
  for (const c of REVENUE_CONCEPTS_PRIORITY) {
    if (perConcept[c] != null) return perConcept[c];
  }
  // Bank fallback: Interest Income + Noninterest Revenue
  const ii  = perConcept["Interest Income"];
  const nir = perConcept["Noninterest Revenue"];
  if (ii != null && nir != null) return ii + nir;
  if (ii != null) return ii;
  return null;
}

/**
 * Returns per-period summaries, sorted oldest → newest, suitable for QoQ/YoY.
 *   [{ fy, fp, label, revenue, eps_basic, eps_diluted }]
 */
export function buildRevenueEpsHistory(data) {
  // Group by (fy, fp); take the first non-null value per concept per period.
  const byPeriod = {};
  data.forEach(row => {
    const key = `${row.fy}|${row.fp || ''}`;
    if (!byPeriod[key]) byPeriod[key] = { fy: row.fy, fp: row.fp, byConcept: {} };
    if (row.value != null && byPeriod[key].byConcept[row.concept] == null) {
      byPeriod[key].byConcept[row.concept] = row.value;
    }
  });

  const PERIOD_ORDER = { Q1: 1, Q2: 2, Q3: 3, Q4: 4, FY: 5 };

  return Object.values(byPeriod)
    .map(p => ({
      fy: p.fy,
      fp: p.fp,
      label: p.fp ? `${p.fy} ${p.fp}` : `${p.fy}`,
      revenue: resolveRevenue(p.byConcept),
      eps_basic:   p.byConcept["Earnings Per Share Basic"]   ?? null,
      eps_diluted: p.byConcept["Earnings Per Share Diluted"] ?? null,
    }))
    .sort((a, b) => {
      if (a.fy !== b.fy) return a.fy - b.fy;
      return (PERIOD_ORDER[a.fp] || 0) - (PERIOD_ORDER[b.fp] || 0);
    });
}

// ── Waterfall rows (ported from earnings-shorts/chart_template.html) ─────────
// Traverses the sankey-map graph and emits an ordered list of rows for a
// horizontal waterfall chart:
//   [{ label, value, type: 'income' | 'cost' }]

const ABBREV = {
  "Revenue": "Revenue",
  "Cost Of Revenue": "Cost of Rev.",
  "Gross Profit": "Gross Profit",
  "Operating Expenses": "Op. Expenses",
  "Selling General & Admin Expenses": "SG&A",
  "Selling General And Administrative Expenses": "SG&A",
  "Selling & Marketing Expenses": "Sales & Mktg",
  "General & Admin Expenses": "G&A",
  "Research & Development": "R&D",
  "R&D Expenses": "R&D",
  "Research And Development Expenses": "R&D",
  "Depreciation & Amortization": "D&A",
  "Operating Income Loss": "Op. Income",
  "Operating Income": "Op. Income",
  "Interest Expense": "Interest Exp.",
  "Interest Income": "Interest Inc.",
  "Net Interest Income": "Net Interest Inc.",
  "Noninterest Revenue": "Non-Int. Revenue",
  "Total Net Revenue": "Total Net Rev.",
  "Provision for Credit Losses": "Prov. Credit Loss",
  "Provision for Loan Losses": "Prov. Loan Loss",
  "Other Operating Expenses": "Other Op. Exp.",
  "Compensation Expense": "Compensation",
  "Technology & Communications": "Tech & Comm.",
  "Occupancy Expense": "Occupancy",
  "Pre-tax Income": "Pre-Tax Inc.",
  "Pre Tax Income": "Pre-Tax Inc.",
  "Income Loss Before Income Taxes Extraordinary Items": "Pre-Tax Inc.",
  "Income Loss Before Income Taxes": "Pre-Tax Inc.",
  "Income Before Income Taxes": "Pre-Tax Inc.",
  "Income Tax": "Inc. Tax",
  "Income Tax Expense": "Inc. Tax",
  "Income Tax Expense Benefit": "Inc. Tax",
  "Net Income Loss": "Net Income",
  "Net Income": "Net Income",
  "Other Income Expense": "Other Inc./Exp.",
  "Non Operating Income Expense": "Non-Op. Inc.",
  "Total Revenue": "Total Revenue",
  "Net Revenue": "Net Revenue",
  "Claims & Losses": "Claims & Losses",
  "Deferred Acquisition Costs Amortization Expense": "DAC Amort.",
  "DAC Amortization": "DAC Amort.",
  "Policy Benefits": "Policy Benefits",
};
const abbr = name => ABBREV[name] || name;

export function buildWaterfallRows(valueMap, sankeyMapData) {
  if (!sankeyMapData || sankeyMapData.length === 0) return [];

  const rows = [];
  const visited = new Set();

  // Collect ALL roots (source_concept = null), deduplicated by target_concept
  let allRoots = [...new Map(
    sankeyMapData.filter(m => !m.source_concept).map(m => [m.target_concept, m])
  ).values()];
  if (!allRoots.length) return [];

  // Sort: roots with outflow children first (e.g. Interest Income before Noninterest Revenue)
  allRoots.sort((a, b) => {
    const aOut = sankeyMapData.some(m => m.source_concept === a.target_concept && m.flow_type === 'outflow');
    const bOut = sankeyMapData.some(m => m.source_concept === b.target_concept && m.flow_type === 'outflow');
    return (bOut ? 1 : 0) - (aOut ? 1 : 0);
  });

  allRoots.forEach(root => {
    let spine = root.target_concept;
    if (valueMap[spine] != null && !visited.has(spine)) {
      rows.push({ label: abbr(spine), value: valueMap[spine], type: 'income' });
      visited.add(spine);
    }
    while (spine) {
      const edges = sankeyMapData.filter(m => m.source_concept === spine);
      // Outflows first (costs)
      edges
        .filter(m => m.flow_type === 'outflow' && valueMap[m.target_concept] != null && !visited.has(m.target_concept))
        .forEach(m => {
          rows.push({ label: abbr(m.target_concept), value: valueMap[m.target_concept], type: 'cost' });
          visited.add(m.target_concept);
        });
      // Then remainder (next spine node)
      const rem = edges.find(m => m.flow_type === 'remainder' && valueMap[m.target_concept] != null && !visited.has(m.target_concept));
      if (!rem) break;
      // Before adding convergence node, insert any other unvisited sources that feed into it
      sankeyMapData
        .filter(m => m.flow_type === 'remainder'
                  && m.target_concept === rem.target_concept
                  && m.source_concept !== spine
                  && !visited.has(m.source_concept)
                  && valueMap[m.source_concept] != null)
        .forEach(m => {
          rows.push({ label: abbr(m.source_concept), value: valueMap[m.source_concept], type: 'income' });
          visited.add(m.source_concept);
        });
      rows.push({ label: abbr(rem.target_concept), value: valueMap[rem.target_concept], type: 'income' });
      visited.add(rem.target_concept);
      spine = rem.target_concept;
    }
  });

  return rows;
}
